import { InvitationState, OrgAssociationType } from '../Enums';
import { NameIdEmailSlim, NameIdSlim } from '../models/custom-models';
import ApiBase from './base/ApiBase'
// import { CheckoutContext, NameIdEmailSlim, NameIdListItem, NameIdSlim } from './Models'

class ManageOrganizationClient extends ApiBase {
    private controllerName: string = 'ManageOrganization';


    async InviteUsersToOrganization(associationType: OrgAssociationType, context: InvitePersonsContext, signal?: AbortSignal): Promise<void> {
        return this.PostAsync(`${this.controllerName}/Invitation?associationType=${associationType}`, context, signal)
    }
    async GetUsers(associationType: OrgAssociationType, signal?: AbortSignal): Promise<OrganizationUser[]> {
        return this.GetAsync(`${this.controllerName}/Users?associationType=${associationType}`, signal);
    }

    async GetInvitationsForOrganization(associationType: OrgAssociationType, signal?: AbortSignal): Promise<InvitationItem[]> {
        return this.GetAsync(`${this.controllerName}/Invitation?associationType=${associationType}`, signal);
    }

    async GetInvitationsByEmail(emailAddress: string, signal?: AbortSignal): Promise<InvitationItem[]> {
        return this.GetAsync(`${this.controllerName}/Invitation/ByEmail?emailAddress=${emailAddress}`, signal);
    }

    async DeleteInvitation(invitationId: string, signal?: AbortSignal): Promise<void> {
        return this.DeleteAsync(`${this.controllerName}/Invitation/${invitationId}`, signal);
    }

    async RemoveUserFromOrganization(userId: string, associationType: OrgAssociationType, signal?: AbortSignal): Promise<void> {
        return this.DeleteAsync(`${this.controllerName}/Users/${userId}?associationType=${associationType}`, signal);
    }

    ///Groups
    async GetGroupList(signal?: AbortSignal): Promise<NameIdSlim[]> {
        return this.GetAsync(`${this.controllerName}/Group`, signal);
    }

    async GetGroupDetail(groupId: string, signal?: AbortSignal): Promise<GroupDetail> {
        return this.GetAsync(`${this.controllerName}/Group/${groupId}`, signal);
    }

    async CreateNewGroup(context: GroupContext, signal?: AbortSignal): Promise<string> {
        return this.PostAsync(`${this.controllerName}/Group`, context, signal)
    }

    async DeleteGroup(groupId: string, signal?: AbortSignal): Promise<void> {
        return this.DeleteAsync(`${this.controllerName}/Group/${groupId}`, signal);
    }

    async GetAvailableUsers(groupId: string, signal?: AbortSignal): Promise<NameIdEmailSlim[]> {
        return this.GetAsync(`${this.controllerName}/Group/${groupId}/AvailableUsers`, signal);
    }

    async AddUserToGroup(groupId: string, userId: string, signal?: AbortSignal): Promise<void> {
        return this.PutAsync(`${this.controllerName}/Group/${groupId}/User/${userId}`, null, signal)
    }

    async RemoveUserFromGroup(groupId: string, userId: string, signal?: AbortSignal): Promise<void> {
        return this.DeleteAsync(`${this.controllerName}/Group/${groupId}/User/${userId}`, signal);
    }

    async UpdateGroup(groupId: string, context: GroupContext, signal?: AbortSignal): Promise<string> {
        return this.PutAsync(`${this.controllerName}/Group/${groupId}`, context, signal)
    }

    async GetPossibleRoles(signal?: AbortSignal): Promise<OrganizationRoleOptions> {
        return this.GetAsync(`${this.controllerName}/PossibleRoles`, signal)
    }

    /// Billing

    async GetBillingSummary(signal?: AbortSignal): Promise<BillingSummary> {
        const response = await this.GetAsync(`Billing/Subscription`, signal) as BillingSubscriptionResponse;
        const formatMoney = (amountInCents: number, currency: string) => new Intl.NumberFormat(
            undefined,
            { style: 'currency', currency: currency.toUpperCase() },
        ).format(amountInCents / 100);

        return {
            HasSubscription: response.HasSubscription,
            IsActive: response.Status === 'Active' || response.Status === 'Trialing',
            Status: response.Status,
            ActiveTeammateCount: response.ActiveTeammates,
            MinimumTeammateCount: response.MinimumQuantity,
            BilledTeammateCount: response.BilledQuantity,
            UnitPriceDisplay: formatMoney(response.UnitAmountCents, response.Currency),
            EstimatedMonthlyTotalDisplay: formatMoney(response.EstimatedMonthlyAmountCents, response.Currency),
            CurrentPeriodEnd: response.CurrentPeriodEnd,
            CancelAtPeriodEnd: response.CancelAtPeriodEnd,
            CanManageBilling: response.PortalAvailable,
        };
    }

    async CreateBillingCheckout(signal?: AbortSignal): Promise<BillingRedirectResponse> {
        return this.PostAsync(`Billing/Checkout`, null, signal)
    }

    async CreateBillingPortal(signal?: AbortSignal): Promise<BillingRedirectResponse> {
        return this.PostAsync(`Billing/Portal`, null, signal)
    }

    async CheckSubscriptionIsActive(signal?: AbortSignal): Promise<boolean> {
        return (await this.GetBillingSummary(signal)).IsActive;
    }
}

export interface BillingSummary {
    HasSubscription: boolean;
    IsActive: boolean;
    Status: string;
    ActiveTeammateCount: number;
    MinimumTeammateCount: number;
    BilledTeammateCount: number;
    UnitPriceDisplay: string;
    EstimatedMonthlyTotalDisplay: string;
    CurrentPeriodEnd?: string;
    CancelAtPeriodEnd: boolean;
    CanManageBilling: boolean;
}

export interface BillingRedirectResponse {
    Url: string;
}

interface BillingSubscriptionResponse {
    HasSubscription: boolean;
    Status: string;
    ActiveTeammates: number;
    MinimumQuantity: number;
    BilledQuantity: number;
    UnitAmountCents: number;
    EstimatedMonthlyAmountCents: number;
    Currency: string;
    CurrentPeriodEnd?: string;
    CancelAtPeriodEnd: boolean;
    PortalAvailable: boolean;
}

export interface OrganizationRoleOptions {
    RoleTemplates: string[];
    SecuredActivities: SecuredActivity[];
}

export interface SecuredActivity {
    Key: string;
    Group: string;
    Name: string;
    Description: string;
    RoleTemplates: string[];
}

export interface GroupContext {
    Name: string;
    Description: string;
}

export interface GroupDetail {
    Id: string;
    Name: string;
    Description: string;
    Members: NameIdEmailSlim[];

    //addon properties
    modified: boolean;
}



export interface OrganizationUser {
    DisplayName: string;
    Id: string;
    OrganizationName: string;
    OrganizationId: string;
    EmailAddress: string;
}

export interface InvitationItem {
    Id: string;
    AssociationType: OrgAssociationType;
    EmailAddress: string;
    OrganizationId: string;
    OrganizationName: string;
    State: InvitationState;
    RoleTemplate: string;
}

export interface InvitePersonsContext {
    EmailAddresses: string[];
    RoleTemplate: string;
}

export default ManageOrganizationClient;
